import type { ReactNode } from 'react';
import { Link } from 'zudoku/components';
import { StatusChip, type SwapsStatus } from './StatusChip';

// Internal navigation uses Zudoku's own <Link> (React Router, basename-aware) rather than a
// raw <a href>, so every card correctly carries the pre-cutover `/_docs` prefix Zudoku's
// router expects (basePath, apps/docs/zudoku.config.tsx) without this component needing to
// know that prefix exists at all — a plain <a href="/get-started"> would silently point at
// the wrong (unprefixed) path pre-cutover, confirmed by clicking it in a sub-path build.

export type ProductTile = {
  title: string;
  status: SwapsStatus;
  description: string;
  href?: string;
};

const cardStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 8,
  padding: 18,
  borderRadius: 'var(--radius)',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'inherit',
  textDecoration: 'none',
};

function Tile({ tile }: { tile: ProductTile }) {
  const body = (
    <>
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap' as const,
        }}
      >
        {tile.title} <StatusChip status={tile.status} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted-foreground)', lineHeight: 1.4 }}>
        {tile.description}
      </div>
    </>
  );
  return tile.href ? (
    <Link to={tile.href} style={cardStyle}>
      {body}
    </Link>
  ) : (
    <div style={cardStyle}>{body}</div>
  );
}

export function ProductGrid({ tiles }: { tiles: ProductTile[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        margin: '16px 0 32px',
      }}
    >
      {tiles.map((tile) => (
        <Tile key={tile.title} tile={tile} />
      ))}
    </div>
  );
}

export type EntryCard = {
  title: string;
  description: string;
  href: string;
  cta: string;
  icon?: ReactNode;
};

export function EntryCards({ cards }: { cards: EntryCard[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
        margin: '8px 0 40px',
      }}
    >
      {cards.map((card) => (
        <Link
          key={card.title}
          to={card.href}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: 22,
            borderRadius: 'var(--radius)',
            background: 'var(--card)',
            border: '1px solid var(--border)',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          {card.icon ? (
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--accent)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {card.icon}
            </span>
          ) : null}
          <span style={{ fontSize: 16, fontWeight: 700 }}>{card.title}</span>
          <span style={{ fontSize: 13.5, color: 'var(--muted-foreground)', lineHeight: 1.4 }}>
            {card.description}
          </span>
          <span
            style={{
              marginTop: 'auto',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--swaps-accent-ink)',
            }}
          >
            {card.cta} →
          </span>
        </Link>
      ))}
    </div>
  );
}
